<template>
  <div class="dashboard-layout">
    <!-- Interactive Reading Session Overlay -->
    <div v-if="activeSession" class="interactive-reading-overlay">
      <InteractiveReadingModule
        :psychic-id="activeSession.psychicId"
        :psychic-name="activeSession.psychicName"
        :session-id="activeSession.sessionId"
        @close="closeActiveSession"
      />
      <button class="close-session-btn" @click="closeActiveSession">✕</button>
    </div>

    <!-- Compact Top Bar: Overview + Sessions -->
    <div class="top-bar">
      <div class="overview-compact">
        <div class="stat-mini">
          <span class="stat-mini-icon">📊</span>
          <span class="stat-mini-value">{{ userStats.totalReadings || 0 }}</span>
          <span class="stat-mini-label">Readings</span>
        </div>
        <div class="stat-mini">
          <span class="stat-mini-icon">💬</span>
          <span class="stat-mini-value">{{ userStats.totalMessages || 0 }}</span>
          <span class="stat-mini-label">Messages</span>
        </div>
        <div class="stat-mini">
          <span class="stat-mini-icon">📅</span>
          <span class="stat-mini-value">{{ userStats.upcomingAppointments || 0 }}</span>
          <span class="stat-mini-label">Upcoming</span>
        </div>
      </div>
      
      <!-- Sessions Queue Compact (for Psychics) -->
      <div v-if="isPsychic" class="sessions-compact">
        <div class="sessions-compact-header">
          <span class="sessions-compact-title">Live Queue</span>
          <span v-if="awaitingSessions.length > 0" class="queue-badge-mini">{{ awaitingSessions.length }}</span>
        </div>
        <div v-if="awaitingSessions.length === 0" class="sessions-compact-empty">No sessions</div>
        <div v-else class="sessions-compact-list">
          <div 
            v-for="session in awaitingSessions.slice(0, 2)" 
            :key="session.sessionId"
            class="session-mini"
            @click="joinSession(session)"
          >
            <span class="session-mini-name">{{ session.userName || 'User' }}</span>
            <span class="session-mini-time">{{ formatTime(session.createdAt) }}</span>
          </div>
        </div>
      </div>

      <!-- Active Sessions Compact (for Users) -->
      <div v-if="!isPsychic && activeUserSessions.length > 0" class="sessions-compact">
        <div class="sessions-compact-header">
          <span class="sessions-compact-title">Active</span>
        </div>
        <div class="sessions-compact-list">
          <div 
            v-for="session in activeUserSessions.slice(0, 2)" 
            :key="session.sessionId"
            class="session-mini"
            @click="resumeSession(session)"
          >
            <span class="session-mini-name">{{ session.psychicName || 'Psychic' }}</span>
            <span class="session-mini-time">{{ formatTime(session.createdAt) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Layout: Left Sidebar + Main Content -->
    <div class="main-layout">
      <!-- Left Sidebar: Quick Actions -->
      <div class="sidebar-actions">
        <button class="sidebar-action" @click="navigateToPage('MessagesModule')">
          <div class="sidebar-action-icon">💬</div>
          <span class="sidebar-action-label">Messages</span>
        </button>
        <button class="sidebar-action" @click="navigateToPage('CalendarModule')">
          <div class="sidebar-action-icon">📅</div>
          <span class="sidebar-action-label">Calendar</span>
        </button>
        <button class="sidebar-action" @click="navigateToPage('SettingsModule')">
          <div class="sidebar-action-icon">⚙️</div>
          <span class="sidebar-action-label">Settings</span>
        </button>
      </div>

      <!-- Main Content: Psychics Explore -->
      <div class="main-content">
        <div class="dashboard-module psychics-module" ref="psychicsModule">
          <div class="module-header">
            <h2>Explore Psychics</h2>
            <div class="module-actions">
              <input 
                type="text" 
                v-model="searchQuery" 
                placeholder="Search psychics..." 
                class="search-input"
              />
            </div>
          </div>

          <div class="category-wheel" ref="categoryWheel">
            <div
              v-for="category in categories"
              :key="category.label"
              class="category-item"
              :class="{ active: selectedCategory === category.label }"
              @click="selectCategory(category.label)"
            >
              <div class="category-icon">
                <img :src="category.icon" :alt="category.label" />
              </div>
              <div class="category-label">
                {{ category.label }}
              </div>
            </div>
          </div>

          <div v-if="noPsychicsAvailable" class="no-psychics-message">
            There are currently no psychics available. Please check another category.
          </div>

          <div class="psychics-grid-wrapper">
            <PsychicModule
              v-if="!noPsychicsAvailable && !selectedPsychic"
              :psychics="filteredPsychics"
              @select="selectPsychic"
            />
          </div>

          <div class="expanded-card-wrapper" v-if="selectedPsychic">
            <PsychicExpandedCard
              :psychic="selectedPsychic"
              @back="deselectPsychic"
              @open-interactive-reading="handleOpenInteractiveReading"
              class="full-width-card"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { firestore } from '@/firebase'
import { collection, getDocs, query, where, doc, getDoc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import PsychicModule from '@/dashboard/PsychicModule.vue';
import PsychicExpandedCard from '@/dashboard/PsychicExpandedCard.vue';
import InteractiveReadingModule from '@/dashboard/InteractiveReadingModule.vue';
import { EventBus } from '../Eventbus.js';

export default {
  components: {
      PsychicModule,
      PsychicExpandedCard,
      InteractiveReadingModule,
  },
  beforeUnmount() {
      EventBus.off('search', this.updateSearchQuery);
      if (this.sessionsUnsubscribe) {
          this.sessionsUnsubscribe();
      }
  },
  data() {
      return {
          categories: [
              { label: "All", icon: require('@/assets/magic_wand.png') },
              { label: "General Readings", icon: require('@/assets/category_icon_turban.png') },
              { label: "Tarot Readings", icon: require('@/assets/category_icon_tarot_card.png') },
              { label: "Lingering Spirits", icon: require('@/assets/category_icon_spirit.png') },
              { label: "Fortune Telling", icon: require('@/assets/category_icon_magic_ball.png') },
              { label: "Palm Readings", icon: require('@/assets/category_icon_palm.png') },
              { label: "Astrology", icon: require('@/assets/category_icon_saturn.png') },
              { label: "Dream Interpretations", icon: require('@/assets/category_icon_dream.png') },
              { label: "Past Lives", icon: require('@/assets/category_icon_ghost.png') },
              { label: "Love & Relationships", icon: require('@/assets/category_icon_heart.png') }
          ],
          selectedPsychic: null,
          psychics: [],
          noPsychicsAvailable: false,
          searchQuery: '',
          selectedCategory: 'All',
          userStats: {
              totalReadings: 0,
              totalMessages: 0,
              upcomingAppointments: 0,
              favoritePsychics: 0
          },
          isPsychic: false,
          currentUserId: null,
          awaitingSessions: [],
          activeUserSessions: [],
          activeSession: null,
          sessionsUnsubscribe: null
      };
  },
  computed: {
      filteredPsychics() {
          const normalizedQuery = this.searchQuery.toLowerCase().trim();
          if (!normalizedQuery) {
              return this.psychics;
          }

          const queryWords = normalizedQuery.split(/\s+/);
          return this.psychics.filter(psychic => {
              const userName = psychic.userName?.toLowerCase() || '';
              const firstName = psychic.firstName?.toLowerCase() || '';
              const lastName = psychic.lastName?.toLowerCase() || '';
              const fullName = `${firstName} ${lastName}`;

              return queryWords.some(queryWord =>
                  userName.includes(queryWord) ||
                  firstName.includes(queryWord) ||
                  lastName.includes(queryWord) ||
                  fullName.includes(queryWord)
              );
          });
      },
  },
  methods: {
      updateSearchQuery(query) {
          this.searchQuery = query;
      },
      async selectCategory(category) {
          this.selectedCategory = category;
          if (category === "All") {
              await this.fetchAllPsychics();
          } else {
              await this.fetchCategoryPsychics(category);
          }
          this.selectedPsychic = null;
      },
      async fetchUserData(userId) {
          try {
              const userRef = doc(firestore, 'users', userId);
              const userDoc = await getDoc(userRef);
              
              if (!userDoc.exists()) {
                  console.warn(`No user found with ID: ${userId}`);
                  return null;
              }

              const userData = userDoc.data();
              return {
                  id: userId,
                  profileImage: userData.profileimgsrc || "",
                  name: userData.displayname || "",
                  bio: userData.bio || "",
                  ratings: userData.psychicrating || null,
                  userType: userData.usertype || "",
                  userName: userData.username || "",
                  displayImgSrc: userData.displayimgsrc || "",
                  email: userData.email || "",
                  firstName: userData.firstname || "",
                  lastName: userData.lastname || "",
                  premium: userData.premium || false,
                  isOnline: userData.isOnline || false,
                  lastOnline: userData.lastOnline ? userData.lastOnline.toDate().toLocaleString() : 'Never',
                  psychicOnDisplay: userData.psychicondisplay || false,
                  psychicOnDisplayCategory: userData.psychicondisplaycategory || "",
                  psychicOriginCountry: userData.psychicorigincountry || null,
                  psychicOriginYear: userData.psychicoriginyear || null,
                  questionsAnswered: userData.questionsAnswered || 0,
                  questionsAsked: userData.questionsAsked || 0,
                  requestCount: userData.requestcount || 0
              };
          } catch (error) {
              console.error("Error fetching user data:", error);
              return null;
          }
      },
      async fetchCategoryPsychics(category) {
          try {
              const psychicsCollection = collection(firestore, `psychicOnDisplay/${category}/psychicsOnDisplay`);
              const snapshot = await getDocs(psychicsCollection);

              if (snapshot.empty) {
                  console.warn("No psychics found in this category.");
                  this.noPsychicsAvailable = true;
                  this.psychics = [];
                  return;
              }

              // Get all psychic IDs from the category
              const psychicIds = snapshot.docs.map(doc => doc.id);
              
              // Fetch full user data for each psychic ID
              const psychicDataPromises = psychicIds.map(id => this.fetchUserData(id));
              const psychicData = await Promise.all(psychicDataPromises);
              
              // Filter out any null results (failed fetches)
              this.psychics = psychicData.filter(data => data !== null);
              this.noPsychicsAvailable = this.psychics.length === 0;
          } catch (error) {
              console.error("Error fetching psychics:", error);
              this.noPsychicsAvailable = true;
          }
      },
      async fetchAllPsychics() {
    try {
        // Fetch users collection from Firestore
        const usersCollection = collection(firestore, 'users');
        // Query to filter users where usertype equals "psychic"
        const q = query(usersCollection, where('usertype', '==', 'psychic'));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            console.warn("No psychics found");
            this.noPsychicsAvailable = true;
            this.psychics = [];
            return;
        }

        // Map through the documents and format the data
        this.psychics = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                profileImage: data.profileimgsrc || "",
                name: data.displayname || "",
                bio: data.bio || "",
                ratings: data.psychicrating || null,
                userType: data.usertype || "",
                userName: data.username || "",
                displayImgSrc: data.displayimgsrc || "",
                email: data.email || "",
                firstName: data.firstname || "",
                lastName: data.lastname || "",
                premium: data.premium || false,
                isOnline: data.isOnline || false,
                lastOnline: data.lastOnline ? data.lastOnline.toDate().toLocaleString() : 'Never',
                psychicOnDisplay: data.psychicondisplay || false,
                psychicOnDisplayCategory: data.psychicondisplaycategory || "",
                psychicOriginCountry: data.psychicorigincountry || null,
                psychicOriginYear: data.psychicoriginyear || null,
                questionsAnswered: data.questionsAnswered || 0,
                questionsAsked: data.questionsAsked || 0,
                requestCount: data.requestcount || 0
            };
        });

        this.noPsychicsAvailable = this.psychics.length === 0;
    } catch (error) {
        console.error("Error fetching psychics:", error);
        this.noPsychicsAvailable = true;
    }
},
      selectPsychic(psychic) {
          this.selectedPsychic = psychic;
      },
      deselectPsychic() {
          this.selectedPsychic = null;
      },
      navigateToPage(page) {
          this.$store.commit('setCurrentPage', page);
      },
      handleOpenInteractiveReading(data) {
          const sessionId = `${this.currentUserId}_${data.psychicId}`;
          this.activeSession = {
              sessionId: sessionId,
              psychicId: data.psychicId,
              psychicName: data.psychicName,
              userId: this.currentUserId
          };
      },
      async fetchUserStats() {
          try {
              const auth = getAuth();
              const user = auth.currentUser;
              if (user) {
                  this.currentUserId = user.uid;
                  const userRef = doc(firestore, 'users', user.uid);
                  const userDoc = await getDoc(userRef);
                  if (userDoc.exists()) {
                      const userData = userDoc.data();
                      this.isPsychic = userData.usertype === 'psychic';
                      
                      // Fetch messages count
                      const messagesRef = collection(firestore, 'users', user.uid, 'messages');
                      const messagesSnapshot = await getDocs(messagesRef);
                      
                      // Fetch appointments count
                      const appointmentsRef = collection(firestore, 'users', user.uid, 'appointments');
                      const appointmentsSnapshot = await getDocs(appointmentsRef);
                      const upcomingAppointments = appointmentsSnapshot.docs.filter(doc => {
                          const data = doc.data();
                          const appointmentDate = data.date?.toDate ? data.date.toDate() : new Date(data.date);
                          return appointmentDate >= new Date();
                      });

                      this.userStats = {
                          totalReadings: userData.requestcount || 0,
                          totalMessages: messagesSnapshot.size,
                          upcomingAppointments: upcomingAppointments.length,
                          favoritePsychics: 0 // Can be implemented later
                      };
                  }
              }
          } catch (error) {
              console.error("Error fetching user stats:", error);
          }
      },
      async fetchAwaitingSessions() {
          if (!this.isPsychic || !this.currentUserId) return;
          
          try {
              const sessionsRef = collection(firestore, 'readingSessions');
              const snapshot = await getDocs(sessionsRef);
              
              const sessionPromises = [];
              snapshot.forEach(docSnap => {
                  const data = docSnap.data();
                  const sessionId = docSnap.id;
                  
                  // Skip ended sessions
                  if (data.ended === true) return;
                  
                  // Check if session is for this psychic (format: userId_psychicId)
                  const parts = sessionId.split('_');
                  if (parts.length >= 2 && parts[1] === this.currentUserId) {
                      // Check if psychic has joined (has userTouches_psychicId)
                      const psychicTouchesKey = `userTouches_${this.currentUserId}`;
                      const hasPsychicJoined = data[psychicTouchesKey] && Object.keys(data[psychicTouchesKey]).length > 0;
                      
                      // Get user info
                      const userId = parts[0];
                      const userTouchesKey = `userTouches_${userId}`;
                      const hasUserActivity = data[userTouchesKey] && Object.keys(data[userTouchesKey]).length > 0;
                      
                      // Show session if: user has activity OR session status is 'awaiting' (just created)
                      const isAwaiting = data.status === 'awaiting' || (!hasPsychicJoined && (hasUserActivity || data.createdAt));
                      
                      if (isAwaiting) {
                          // Fetch user name
                          sessionPromises.push(
                              this.fetchUserData(userId).then(userData => {
                                  if (userData) {
                                      return {
                                          sessionId,
                                          userId,
                                          userName: userData.name || userData.userName || 'User',
                                          createdAt: data.createdAt || data.lastUpdate || new Date(),
                                          psychicId: this.currentUserId,
                                          status: data.status || 'active'
                                      };
                                  }
                                  return null;
                              })
                          );
                      }
                  }
              });
              
              const sessions = await Promise.all(sessionPromises);
              this.awaitingSessions = sessions.filter(s => s !== null);
          } catch (error) {
              console.error("Error fetching awaiting sessions:", error);
          }
      },
      async fetchActiveUserSessions() {
          if (this.isPsychic || !this.currentUserId) return;
          
          try {
              const sessionsRef = collection(firestore, 'readingSessions');
              const snapshot = await getDocs(sessionsRef);
              
              const sessionPromises = [];
              snapshot.forEach(docSnap => {
                  const data = docSnap.data();
                  const sessionId = docSnap.id;
                  
                  // Skip ended sessions
                  if (data.ended === true) return;
                  
                  // Check if session is for this user (format: userId_psychicId)
                  const parts = sessionId.split('_');
                  if (parts.length >= 2 && parts[0] === this.currentUserId) {
                      const psychicId = parts[1];
                      const userTouchesKey = `userTouches_${this.currentUserId}`;
                      const hasUserActivity = data[userTouchesKey] && Object.keys(data[userTouchesKey]).length > 0;
                      
                      // Show session if it exists and hasn't ended
                      if (data.createdAt || hasUserActivity) {
                          // Fetch psychic info
                          sessionPromises.push(
                              this.fetchUserData(psychicId).then(psychicData => {
                                  if (psychicData) {
                                      return {
                                          sessionId,
                                          psychicId,
                                          psychicName: psychicData.name || psychicData.userName || 'Psychic',
                                          createdAt: data.createdAt || data.lastUpdate || new Date(),
                                          userId: this.currentUserId,
                                          status: data.status || 'active'
                                      };
                                  }
                                  return null;
                              })
                          );
                      }
                  }
              });
              
              const sessions = await Promise.all(sessionPromises);
              this.activeUserSessions = sessions.filter(s => s !== null);
          } catch (error) {
              console.error("Error fetching active user sessions:", error);
          }
      },
      listenForSessions() {
          if (!this.currentUserId) return;
          
          const sessionsRef = collection(firestore, 'readingSessions');
          this.sessionsUnsubscribe = onSnapshot(sessionsRef, () => {
              if (this.isPsychic) {
                  this.fetchAwaitingSessions();
              } else {
                  this.fetchActiveUserSessions();
              }
          });
      },
      async joinSession(session) {
          // For psychics joining: sessionId format is userId_psychicId
          // The psychic's ID is this.currentUserId, and they're reading for session.userId
          // But InteractiveReadingModule expects psychicId to be the other person's ID for listening
          // Actually, looking at the module, it listens for userTouches_${psychicId}
          // So when psychic joins, psychicId should be the user's ID (to listen to user touches)
          // And the psychic sends as userTouches_${currentUserId} where currentUserId is psychic's ID
          this.activeSession = {
              sessionId: session.sessionId,
              psychicId: session.userId, // The user they're reading for
              psychicName: session.userName || 'User',
              userId: session.userId
          };
          
          // Mark session as active and update status
          if (session.sessionId) {
              const sessionRef = doc(firestore, 'readingSessions', session.sessionId);
              await setDoc(sessionRef, {
                  psychicJoined: true,
                  status: 'active',
                  lastUpdate: serverTimestamp()
              }, { merge: true });
          }
      },
      resumeSession(session) {
          this.activeSession = {
              sessionId: session.sessionId,
              psychicId: session.psychicId,
              psychicName: session.psychicName,
              userId: session.userId || this.currentUserId
          };
      },
      async closeActiveSession() {
          // Mark session as ended if it exists
          if (this.activeSession && this.activeSession.sessionId) {
              try {
                  const sessionRef = doc(firestore, 'readingSessions', this.activeSession.sessionId);
                  await setDoc(
                      sessionRef,
                      {
                          ended: true,
                          endedBy: this.currentUserId,
                          endedAt: serverTimestamp(),
                          lastUpdate: serverTimestamp()
                      },
                      { merge: true }
                  );
              } catch (error) {
                  console.error("Error closing session:", error);
              }
          }
          this.activeSession = null;
      },
      formatTime(timestamp) {
          if (!timestamp) return 'Just now';
          const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
          const now = new Date();
          const diff = now - date;
          const minutes = Math.floor(diff / 60000);
          
          if (minutes < 1) return 'Just now';
          if (minutes < 60) return `${minutes}m ago`;
          const hours = Math.floor(minutes / 60);
          if (hours < 24) return `${hours}h ago`;
          return date.toLocaleDateString();
      }
  },
  mounted() {
      EventBus.on('search', this.updateSearchQuery);
      this.fetchAllPsychics();
      this.fetchUserStats().then(() => {
          if (this.isPsychic) {
              this.fetchAwaitingSessions();
          } else {
              this.fetchActiveUserSessions();
          }
          this.listenForSessions();
      });
  }
};
</script>
<style scoped>
.dashboard-layout {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(22, 4, 30, 1) 0%, rgba(42, 9, 58, 1) 100%);
  position: relative;
}

/* Compact Top Bar */
.top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background: linear-gradient(135deg, rgba(42, 9, 58, 0.98) 0%, rgba(22, 4, 30, 0.98) 100%);
  border-bottom: 1px solid rgba(219, 143, 255, 0.15);
  gap: 10px;
  flex-shrink: 0;
  min-height: 44px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.overview-compact {
  display: flex;
  gap: 6px;
  align-items: center;
  flex: 1;
}

.stat-mini {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  padding: 4px 8px;
  background: rgba(219, 143, 255, 0.08);
  border-radius: 6px;
  border: 1px solid rgba(219, 143, 255, 0.15);
  min-width: 55px;
  transition: all 0.2s ease;
}

.stat-mini:hover {
  background: rgba(219, 143, 255, 0.12);
  border-color: rgba(219, 143, 255, 0.25);
  transform: translateY(-1px);
}

.stat-mini-icon {
  font-size: 14px;
  line-height: 1;
}

.stat-mini-value {
  font-size: 16px;
  font-weight: 800;
  color: #DB8FFF;
  line-height: 1;
}

.stat-mini-label {
  font-size: 8px;
  color: #ECC3FF;
  opacity: 0.75;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  font-weight: 600;
}

.sessions-compact {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 8px;
  background: rgba(219, 143, 255, 0.08);
  border-radius: 6px;
  border: 1px solid rgba(219, 143, 255, 0.15);
  min-width: 140px;
  max-width: 180px;
  transition: all 0.2s ease;
}

.sessions-compact:hover {
  background: rgba(219, 143, 255, 0.12);
  border-color: rgba(219, 143, 255, 0.25);
}

.sessions-compact-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.sessions-compact-title {
  font-size: 10px;
  font-weight: 700;
  color: #DB8FFF;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.queue-badge-mini {
  background: linear-gradient(135deg, #DB8FFF 0%, #690099 100%);
  color: #000000;
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 9px;
  font-weight: 700;
  line-height: 1.2;
}

.sessions-compact-empty {
  font-size: 9px;
  color: #ECC3FF;
  opacity: 0.6;
  font-style: italic;
}

.sessions-compact-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.session-mini {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px;
  background: rgba(22, 4, 30, 0.5);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid rgba(219, 143, 255, 0.1);
}

.session-mini:hover {
  background: rgba(219, 143, 255, 0.15);
  transform: translateX(2px);
  border-color: rgba(219, 143, 255, 0.3);
}

.session-mini-name {
  font-size: 10px;
  font-weight: 600;
  color: #DB8FFF;
}

.session-mini-time {
  font-size: 9px;
  color: #ECC3FF;
  opacity: 0.7;
}

/* Main Layout */
.main-layout {
  display: flex;
  flex: 1;
  overflow: hidden;
  gap: 0;
}

/* Left Sidebar: Quick Actions */
.sidebar-actions {
  display: flex;
  flex-direction: column;
  width: 60px;
  padding: 8px 6px;
  background: linear-gradient(180deg, rgba(42, 9, 58, 0.98) 0%, rgba(22, 4, 30, 0.98) 100%);
  border-right: 1px solid rgba(219, 143, 255, 0.15);
  gap: 6px;
  flex-shrink: 0;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
}

.sidebar-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px 4px;
  background: rgba(219, 143, 255, 0.08);
  border: 1px solid rgba(219, 143, 255, 0.15);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  color: #ECC3FF;
  position: relative;
  overflow: hidden;
}

.sidebar-action::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(219, 143, 255, 0.2) 0%, rgba(106, 0, 153, 0.2) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.sidebar-action:hover {
  background: rgba(219, 143, 255, 0.15);
  border-color: rgba(219, 143, 255, 0.4);
  transform: translateX(2px) scale(1.03);
  box-shadow: 0 4px 12px rgba(219, 143, 255, 0.25);
}

.sidebar-action:hover::before {
  opacity: 1;
}

.sidebar-action-icon {
  font-size: 18px;
  position: relative;
  z-index: 1;
  line-height: 1;
}

.sidebar-action-label {
  font-size: 8px;
  font-weight: 600;
  text-align: center;
  position: relative;
  z-index: 1;
  letter-spacing: 0.1px;
}

/* Main Content Area */
.main-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 0;
}

.dashboard-module {
  background: linear-gradient(135deg, rgba(42, 9, 58, 0.98) 0%, rgba(22, 4, 30, 0.98) 100%);
  backdrop-filter: blur(10px);
  border-radius: 0;
  padding: 0;
  box-shadow: none;
  border: none;
  border-right: 1px solid rgba(219, 143, 255, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.dashboard-module::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #DB8FFF, #690099, #DB8FFF);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.dashboard-module:hover {
  transform: none;
  box-shadow: none;
  border-color: rgba(219, 143, 255, 0.1);
}

.dashboard-module:hover::before {
  opacity: 1;
}

.module-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0;
  padding: 8px 10px;
  border-bottom: 1px solid rgba(219, 143, 255, 0.15);
  flex-shrink: 0;
}

.module-header h2 {
  font-size: 16px;
  font-weight: 800;
  color: #DB8FFF;
  margin: 0;
  letter-spacing: -0.2px;
  text-shadow: 0 1px 4px rgba(219, 143, 255, 0.2);
}

.module-actions {
  display: flex;
  gap: 8px;
}

.search-input {
  padding: 6px 10px;
  background: rgba(22, 4, 30, 0.6);
  border: 1px solid rgba(219, 143, 255, 0.2);
  border-radius: 6px;
  color: #ECC3FF;
  font-size: 12px;
  width: 200px;
  transition: all 0.2s ease;
}

.search-input:hover {
  border-color: rgba(219, 143, 255, 0.5);
}

.search-input:focus {
  outline: none;
  border-color: #DB8FFF;
  box-shadow: 0 0 0 2px rgba(219, 143, 255, 0.2);
  background: rgba(22, 4, 30, 0.8);
}

.search-input::placeholder {
  color: rgba(236, 195, 255, 0.5);
}

/* Stats Module */
.stats-module {
  grid-column: span 2;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.stat-card {
  background: linear-gradient(135deg, rgba(219, 143, 255, 0.1) 0%, rgba(106, 0, 153, 0.1) 100%);
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid rgba(219, 143, 255, 0.2);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(219, 143, 255, 0.08) 0%, rgba(106, 0, 153, 0.08) 100%);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.stat-card:hover {
  background: linear-gradient(135deg, rgba(219, 143, 255, 0.18) 0%, rgba(106, 0, 153, 0.18) 100%);
  border-color: rgba(219, 143, 255, 0.4);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(219, 143, 255, 0.2);
}

.stat-card:hover::before {
  opacity: 1;
}

.stat-icon {
  font-size: 28px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(219, 143, 255, 0.2) 0%, rgba(106, 0, 153, 0.2) 100%);
  border-radius: 10px;
  border: 1px solid rgba(219, 143, 255, 0.25);
  position: relative;
  z-index: 1;
  flex-shrink: 0;
}

.stat-content {
  flex: 1;
  position: relative;
  z-index: 1;
}

.stat-value {
  font-size: 28px;
  font-weight: 800;
  color: #DB8FFF;
  line-height: 1;
  margin-bottom: 4px;
  text-shadow: 0 1px 6px rgba(219, 143, 255, 0.3);
  letter-spacing: -0.5px;
}

.stat-label {
  font-size: 11px;
  color: #ECC3FF;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

/* Actions Module */
.actions-module {
  grid-column: span 1;
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.action-card {
  background: linear-gradient(135deg, rgba(22, 4, 30, 0.6) 0%, rgba(42, 9, 58, 0.6) 100%);
  border: 1px solid rgba(219, 143, 255, 0.2);
  border-radius: 12px;
  padding: 14px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  color: #ECC3FF;
  font-family: inherit;
  position: relative;
  overflow: hidden;
}

.action-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(219, 143, 255, 0.1) 0%, rgba(106, 0, 153, 0.1) 100%);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.action-card:hover {
  background: linear-gradient(135deg, rgba(219, 143, 255, 0.15) 0%, rgba(106, 0, 153, 0.15) 100%);
  border-color: rgba(219, 143, 255, 0.4);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(219, 143, 255, 0.2);
}

.action-card:hover::before {
  opacity: 1;
}

.action-icon {
  font-size: 28px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(219, 143, 255, 0.2) 0%, rgba(106, 0, 153, 0.2) 100%);
  border-radius: 10px;
  border: 1px solid rgba(219, 143, 255, 0.25);
  position: relative;
  z-index: 1;
}

.action-label {
  font-size: 12px;
  font-weight: 600;
  color: #ECC3FF;
  position: relative;
  z-index: 1;
  letter-spacing: 0.2px;
}

/* Psychics Module */
.psychics-module {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

.psychics-grid-wrapper {
  margin: 0;
  flex: 1;
  overflow: hidden;
  min-height: 0;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
}

.psychics-grid-wrapper::-webkit-scrollbar {
  width: 6px;
}

.psychics-grid-wrapper::-webkit-scrollbar-track {
  background: rgba(22, 4, 30, 0.3);
  border-radius: 3px;
}

.psychics-grid-wrapper::-webkit-scrollbar-thumb {
  background: rgba(219, 143, 255, 0.3);
  border-radius: 3px;
}

.psychics-grid-wrapper::-webkit-scrollbar-thumb:hover {
  background: rgba(219, 143, 255, 0.5);
}

.expanded-card-wrapper {
  margin: 0;
  width: 100%;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  padding: 8px 10px;
}

.expanded-card-wrapper::-webkit-scrollbar {
  width: 6px;
}

.expanded-card-wrapper::-webkit-scrollbar-track {
  background: rgba(22, 4, 30, 0.3);
  border-radius: 3px;
}

.expanded-card-wrapper::-webkit-scrollbar-thumb {
  background: rgba(219, 143, 255, 0.3);
  border-radius: 3px;
}

.expanded-card-wrapper::-webkit-scrollbar-thumb:hover {
  background: rgba(219, 143, 255, 0.5);
}

.full-width-card {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* 'No Psychics Available' message */
.no-psychics-message {
  padding: 16px;
  text-align: center;
}

/* Logo styling */
.logo img {
  height: 50px;
  max-width: 100%;
}

/* Profile bubble styles */
.profile-bubble {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
}

.profile-bubble img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Contact Wheel Styles */
.contact-wheel-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 80px;
  position: relative;
  overflow-x: auto;
  scrollbar-width: none;
}

.contact-wheel {
  display: flex;
  overflow-x: scroll;
  scroll-behavior: smooth;
  padding: 0;
  max-width: 100%;
  -ms-overflow-style: none;
}

.contact-wheel::-webkit-scrollbar {
  display: none;
}

.contact-item {
  flex: 0 0 auto;
  margin: 0 10px;
  text-align: center;
}

.contact-image {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 2px solid #6a0dad;
}

.scroll-btn {
  background: none;
  border: none;
  color: #6a0dad;
  font-size: 30px;
  cursor: pointer;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
}

.left-btn {
  left: 10px;
}

.right-btn {
  right: 10px;
}

.scroll-btn:hover {
  color: #50108a;
}

/* Category Wheel */
.category-wheel {
  display: flex;
  justify-content: flex-start;
  gap: 6px;
  width: 100%;
  white-space: nowrap;
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(219, 143, 255, 0.3) transparent;
  padding: 6px 10px;
  flex-shrink: 0;
  margin: 0;
  border-bottom: 1px solid rgba(219, 143, 255, 0.1);
}

.category-wheel::-webkit-scrollbar {
  height: 6px;
}

.category-wheel::-webkit-scrollbar-track {
  background: transparent;
}

.category-wheel::-webkit-scrollbar-thumb {
  background: rgba(219, 143, 255, 0.3);
  border-radius: 3px;
}

.category-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px 10px;
  font-size: 10px;
  color: #ECC3FF;
  border-radius: 6px;
  cursor: pointer;
  text-align: center;
  white-space: nowrap;
  transition: all 0.2s ease;
  background: rgba(22, 4, 30, 0.5);
  border: 1px solid rgba(219, 143, 255, 0.15);
  min-width: 60px;
}

.category-item:hover {
  background: rgba(219, 143, 255, 0.12);
  border-color: rgba(219, 143, 255, 0.35);
  transform: translateY(-1px);
}

.category-item.active {
  background: rgba(219, 143, 255, 0.18);
  border-color: rgba(219, 143, 255, 0.5);
  color: #DB8FFF;
  box-shadow: 0 2px 8px rgba(219, 143, 255, 0.2);
}

.category-icon {
  margin-bottom: 4px;
}

.category-icon img {
  width: 24px;
  height: 24px;
  object-fit: contain;
  filter: brightness(0) saturate(100%) invert(85%) sepia(30%) saturate(2000%) hue-rotate(250deg) brightness(110%) contrast(110%);
}

.category-item.active .category-icon img {
  filter: brightness(0) saturate(100%) invert(70%) sepia(50%) saturate(2000%) hue-rotate(250deg) brightness(120%) contrast(120%);
}

.no-psychics-message {
  padding: 10px;
  text-align: center;
  color: #ECC3FF;
  opacity: 0.7;
  font-size: 12px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-sessions-message {
  padding: 12px;
  text-align: center;
  color: #ECC3FF;
  opacity: 0.7;
  font-size: 12px;
  font-style: italic;
}

/* Interactive Reading Overlay */
.interactive-reading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 2000;
  background: #000000;
}

/* Sessions Queue Module */
.sessions-queue-module,
.active-sessions-module {
  grid-column: 1 / -1;
}

.queue-badge {
  background: linear-gradient(135deg, #DB8FFF 0%, #690099 100%);
  color: #000000;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  box-shadow: 0 2px 6px rgba(219, 143, 255, 0.3);
}

.sessions-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.session-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: linear-gradient(135deg, rgba(22, 4, 30, 0.6) 0%, rgba(42, 9, 58, 0.6) 100%);
  border: 1px solid rgba(219, 143, 255, 0.2);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.session-item:hover {
  background: linear-gradient(135deg, rgba(219, 143, 255, 0.12) 0%, rgba(106, 0, 153, 0.12) 100%);
  border-color: rgba(219, 143, 255, 0.4);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(219, 143, 255, 0.15);
}

.session-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.session-user-name {
  font-size: 14px;
  font-weight: 700;
  color: #DB8FFF;
  line-height: 1.2;
}

.session-time {
  font-size: 11px;
  color: #ECC3FF;
  opacity: 0.7;
}

.join-session-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #DB8FFF 0%, #690099 100%);
  border: none;
  border-radius: 8px;
  color: #000000;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(219, 143, 255, 0.25);
  white-space: nowrap;
}

.join-session-btn:hover {
  background: linear-gradient(135deg, #ECC3FF 0%, #DB8FFF 100%);
  transform: scale(1.03);
  box-shadow: 0 4px 12px rgba(219, 143, 255, 0.4);
}

.join-session-btn:active {
  transform: scale(0.98);
}

.close-session-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  background: rgba(255, 77, 77, 0.8);
  border: 1px solid rgba(255, 77, 77, 0.5);
  border-radius: 50%;
  color: #FFFFFF;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  z-index: 2001;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.close-session-btn:hover {
  background: rgba(255, 77, 77, 1);
  transform: scale(1.08);
  box-shadow: 0 2px 8px rgba(255, 77, 77, 0.4);
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .dashboard-layout {
    height: 100vh;
    overflow: hidden;
  }

  .top-bar {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 16px;
    min-height: auto;
  }

  .overview-compact {
    width: 100%;
    justify-content: space-between;
    gap: 12px;
  }

  .stat-mini {
    flex: 1;
    min-width: 0;
    padding: 6px 10px;
  }

  .stat-mini-value {
    font-size: 18px;
  }

  .stat-mini-label {
    font-size: 10px;
  }

  .sessions-compact {
    width: 100%;
    max-width: 100%;
  }

  .main-layout {
    flex-direction: column;
  }

  .sidebar-actions {
    flex-direction: row;
    width: 100%;
    padding: 12px;
    border-right: none;
    border-bottom: 2px solid rgba(219, 143, 255, 0.2);
    justify-content: space-around;
  }

  .sidebar-action {
    flex: 1;
    padding: 12px 8px;
  }

  .sidebar-action-icon {
    font-size: 24px;
  }

  .sidebar-action-label {
    font-size: 10px;
  }

  .main-content {
    padding: 12px;
    flex: 1;
    overflow-y: auto;
  }

  .dashboard-module {
    padding: 16px;
    height: auto;
  }

  .module-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
    padding-bottom: 12px;
  }

  .module-header h2 {
    font-size: 20px;
  }

  .search-input {
    width: 100%;
    font-size: 14px;
    padding: 10px 16px;
  }

  .category-item {
    padding: 8px 12px;
    font-size: 10px;
    min-width: 60px;
  }

  .category-icon img {
    width: 20px;
    height: 20px;
  }

  .psychics-grid-wrapper {
    margin-top: 12px;
  }
}

@media (max-width: 480px) {
  .top-bar {
    padding: 10px 12px;
  }

  .overview-compact {
    gap: 8px;
  }

  .stat-mini {
    padding: 6px 8px;
  }

  .stat-mini-icon {
    font-size: 16px;
  }

  .stat-mini-value {
    font-size: 16px;
  }

  .stat-mini-label {
    font-size: 9px;
  }

  .sessions-compact {
    padding: 6px 12px;
  }

  .session-mini {
    padding: 4px 8px;
  }

  .session-mini-name {
    font-size: 11px;
  }

  .session-mini-time {
    font-size: 9px;
  }

  .sidebar-actions {
    padding: 8px;
    gap: 8px;
  }

  .sidebar-action {
    padding: 10px 6px;
  }

  .sidebar-action-icon {
    font-size: 20px;
  }

  .sidebar-action-label {
    font-size: 9px;
  }

  .main-content {
    padding: 8px;
  }

  .dashboard-module {
    padding: 12px;
    border-radius: 16px;
  }

  .module-header h2 {
    font-size: 18px;
  }

  .category-item {
    padding: 6px 10px;
    min-width: 55px;
  }

  .category-icon img {
    width: 18px;
    height: 18px;
  }
}
</style>
